import { useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { axios } from "setups/axios";

import ChangeExpiredPasswordModal from "./ChangeExpiredPasswordModal";

const Login = () => {
  const [data, setData] = useState({
    SAP_ID: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [expiredPasswordContext, setExpiredPasswordContext] = useState(null);

  const validate = () => {
    let error = "";

    if (!data.SAP_ID || !data.password)
      error = "Please provide both SAP ID and Password.";

    setError(error);

    if (error) return false;

    return true;
  };

  const handlePasswordExpired = (responseData) => {
    setExpiredPasswordContext({
      SAP_ID: responseData?.SAP_ID || data.SAP_ID,
      currentPin: data.password,
    });
    setError("");
    setShowChangePasswordModal(true);
  };

  const loginHandler = async () => {
    if (!validate()) return;

    try {
      const res = await axios()({
        url: `/api/v1/admin/login`,
        method: "POST",
        data,
      });

      // Token/user live under res.data.data.
      const responseData = res.data?.data;

      // Some backends flag an expired password on a 200 response.
      if (responseData?.passwordExpired) {
        handlePasswordExpired(responseData);
        return;
      }

      if (responseData?.token) {
        localStorage.setItem("token", responseData.token);
        
        // Save the zone to localStorage for the axios interceptor
        const zone = responseData.zone || (responseData.scope?.zones?.length ? responseData.scope.zones.join(",") : null);
        if (zone) {
          localStorage.setItem("admin_zone", zone);
        } else {
          localStorage.removeItem("admin_zone");
        }
        
        // Reload so fetchUser() (GET /api/v1/user) hydrates role/permissions/scope.
        location.reload();
      }
    } catch (error) {
      console.log(error.response);

      const resp = error?.response;
      const payload = resp?.data?.data ?? resp?.data ?? {};

      // 403 → password expired: open the change-password modal.
      if (resp?.status === 403 || payload?.passwordExpired) {
        handlePasswordExpired(payload);
        return;
      }

      // 400 (bad credentials / validation) → surface the server message.
      setError(resp?.data?.message || "Login failed. Please try again.");
    }
  };

  return (
    <Box id="login-container" sx={{ width: "100%" }}>
      <Stack spacing={2.5}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          id="sap-id"
          fullWidth
          label="SAP ID"
          placeholder="SAP ID"
          value={data.SAP_ID}
          onChange={(e) => setData({ ...data, SAP_ID: e.target.value })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          id="pin"
          fullWidth
          type="password"
          label="Password"
          placeholder="Password"
          value={data.password}
          onChange={(e) => setData({ ...data, password: e.target.value })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          id="submit-button"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={loginHandler}
          sx={{ fontWeight: 700 }}
        >
          Login
        </Button>
      </Stack>

      <ChangeExpiredPasswordModal
        open={showChangePasswordModal}
        SAP_ID={expiredPasswordContext?.SAP_ID}
        currentPin={expiredPasswordContext?.currentPin}
      />
    </Box>
  );
};

export default Login;
